import type { DeviceFacts, PresenceSession } from '@api/devices/contracts';
import { ago, duration, MINUTE } from '../lib/time';

/** Reports come every minute; older than this, the line says when it was true. */
const CURRENT_MS = 2 * MINUTE;

/**
 * Who is at the computer, for a technician choosing a quiet moment (Harry, 2026-10-21, WRMM-159). Information only:
 * nothing waits on it. `asOf` is set once the report is no longer current; the idle time is as reported, not guessed on.
 */
export function describePresence(presence: DeviceFacts['presence'], now = Date.now()): { text: string; asOf: string | null } | null {
  if (!presence) return null;
  const asOf = now - Date.parse(presence.observedAt) >= CURRENT_MS ? ago(presence.observedAt, now) : null;
  if (!presence.sessions.length) return { text: 'No one signed in', asOf };
  return { text: presence.sessions.map(s => `${s.user}: ${state(s)}`).join(' · '), asOf };
}

function state(session: PresenceSession) {
  const idle = session.idleSeconds === null ? null : session.idleSeconds * 1000;
  // A locked screen says enough until it has been idle a while.
  const activity = idle === null || (session.locked && idle < MINUTE) ? (session.locked ? null : 'signed in')
    : idle < MINUTE ? 'active now' : `idle for ${duration(idle)}`;
  return [session.locked ? 'locked' : null, activity].filter(Boolean).join(', ');
}
